import { useState, useEffect } from "react";
import { Users } from "lucide-react";
import { BaseModal } from "./BaseModal";

export function HouseholdSizeModal({
  isOpen,
  onClose,
  onSave,
  currentHouseholdSize,
}) {
  const [householdSize, setHouseholdSize] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setHouseholdSize(currentHouseholdSize?.toString() || "");
      setError("");
    }
  }, [isOpen, currentHouseholdSize]);

  const validateHouseholdSize = (value) => {
    if (!value) return "Household size is required";
    if (!/^\d+$/.test(value)) return "Please enter a valid number";
    const numValue = parseInt(value, 10);
    if (numValue < 1 || numValue > 99) return "Household size must be between 1 and 99";
    return null;
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setHouseholdSize(value);
      setError("");
    }
  };

  const handleSave = () => {
    const validationError = validateHouseholdSize(householdSize);
    if (validationError) {
      setError(validationError);
      return;
    }
    onSave(parseInt(householdSize, 10));
    handleClose();
  };

  const handleClose = () => {
    setHouseholdSize("");
    setError("");
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    else if (e.key === "Escape") handleClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Household Size"
      titleIcon={<Users className="w-5 h-5" />}
      footer={
        <>
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Save
          </button>
        </>
      }
    >
      <p className="text-sm text-gray-600 mb-4">
        Set the number of people in your household to help us understand if
        there is more food in a recipe than needed.
      </p>

      <div>
        <label
          htmlFor="household-size-input"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Number of people
        </label>
        <input
          id="household-size-input"
          type="text"
          inputMode="numeric"
          pattern="\d*"
          value={householdSize}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter number (1-99)"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? "border-red-500" : "border-gray-300"
          }`}
          autoFocus
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>
    </BaseModal>
  );
}
